/**
 * เอกสารบริษัท (พนักงาน) — เลือกเอกสาร + ระบุวัตถุประสงค์ → ยื่นคำขอสำเนา
 * + รายการคำขอของฉัน (สถานะ + ดาวน์โหลดเมื่ออนุมัติ)
 */
import { useState } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { useToast } from '../components/ui/toast';
import {
  useMyCompanyDocuments,
  useMyCompanyDocRequests,
  useSubmitCompanyDocRequest,
  useDownloadMyCompanyDocRequest,
} from '../hooks';
import { FileText, Send, Download } from 'lucide-react';
import { openWhenReady } from '../lib/open-when-ready';

const STATUS_LABEL: Record<string, string> = {
  pending: 'รออนุมัติ',
  approved: 'อนุมัติแล้ว',
  rejected: 'ปฏิเสธ',
  canceled: 'ยกเลิก',
};
const STATUS_CLASS: Record<string, string> = {
  pending: 'text-amber-700 bg-amber-50',
  approved: 'text-green-700 bg-green-50',
  rejected: 'text-red-700 bg-red-50',
  canceled: 'text-gray-500 bg-gray-100',
};

export function CompanyDocs() {
  const { data: docsData, isLoading: docsLoading } = useMyCompanyDocuments();
  const { data: reqData, isLoading: reqLoading } = useMyCompanyDocRequests();
  const submitM = useSubmitCompanyDocRequest();
  const downloadM = useDownloadMyCompanyDocRequest();
  const toast = useToast();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [purpose, setPurpose] = useState('');
  const [recipient, setRecipient] = useState('');

  const documents = docsData?.documents ?? [];
  const requests = reqData?.requests ?? [];

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleSubmit() {
    if (selected.size === 0) { toast.error('เลือกเอกสารอย่างน้อย 1 รายการ'); return; }
    if (!purpose.trim()) { toast.error('กรุณาระบุวัตถุประสงค์'); return; }
    try {
      await submitM.mutateAsync({ doc_ids: [...selected], purpose: purpose.trim(), recipient: recipient.trim() || undefined });
      toast.success('ยื่นคำขอแล้ว รอเจ้าของอนุมัติ');
      setSelected(new Set());
      setPurpose('');
      setRecipient('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ยื่นคำขอไม่สำเร็จ');
    }
  }

  async function handleDownload(id: string) {
    try {
      await openWhenReady(async () => (await downloadM.mutateAsync(id)).url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ดาวน์โหลดไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="เอกสารบริษัท">
      <p className="text-sm text-gray-600 mb-3">เลือกเอกสารที่ต้องการสำเนา ระบุวัตถุประสงค์ แล้วยื่นคำขอ — เจ้าของบริษัทจะเป็นผู้อนุมัติ</p>

      <div className="card p-4 space-y-3 mb-4">
        <div className="font-semibold text-sm">เลือกเอกสาร</div>
        {docsLoading ? (
          <p className="text-sm text-gray-400">กำลังโหลด…</p>
        ) : documents.length === 0 ? (
          <p className="text-sm text-gray-400 italic">ยังไม่มีเอกสารบริษัทให้เลือก</p>
        ) : (
          <div className="space-y-1">
            {documents.map((d) => (
              <label key={d.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} />
                <FileText className="w-4 h-4 text-gray-400" />
                {d.label}
              </label>
            ))}
          </div>
        )}

        <div>
          <label className="label">วัตถุประสงค์ *</label>
          <input
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="เช่น ใช้ประกอบการขึ้นทะเบียนคู่ค้า"
            className="input w-full"
          />
        </div>
        <div>
          <label className="label">ส่งให้ (ไม่บังคับ)</label>
          <input value={recipient} onChange={(e) => setRecipient(e.target.value)} className="input w-full" />
        </div>
        <button onClick={handleSubmit} disabled={submitM.isPending} className="btn-cta w-full">
          <Send className="w-4 h-4" /> {submitM.isPending ? 'กำลังยื่น…' : 'ยื่นคำขอ'}
        </button>
      </div>

      <div className="font-semibold text-sm mb-2">คำขอของฉัน</div>
      {reqLoading ? (
        <p className="text-sm text-gray-400">กำลังโหลด…</p>
      ) : requests.length === 0 ? (
        <p className="text-sm text-gray-400 italic">ยังไม่มีคำขอ</p>
      ) : (
        <div className="card divide-y divide-gray-100">
          {requests.map((r) => (
            <div key={r.id} className="px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-gray-400">{r.req_no}</span>
                <span className={`whitespace-nowrap text-xs font-medium rounded-full px-2.5 py-1 ${STATUS_CLASS[r.status] ?? ''}`}>
                  {STATUS_LABEL[r.status] ?? r.status}
                </span>
              </div>
              <div className="text-sm text-gray-700 mt-1">{r.purpose}</div>
              {r.status === 'rejected' && r.decision_note && (
                <div className="text-xs text-red-600 mt-1">เหตุผล: {r.decision_note}</div>
              )}
              {r.status === 'approved' && (
                <button onClick={() => handleDownload(r.id)} className="btn-secondary mt-2 text-xs">
                  <Download className="w-3.5 h-3.5" /> ดาวน์โหลดสำเนา
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
